import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@ludus/ui'
import type { PipelineApiError } from '@/lib/api/pipelines.ts'
import { useCreateManager } from '../hooks/useCreateManager.ts'
import { saveErrorKey } from './saveError.ts'

interface NewManagerDialogProps {
  onOpenChange: (next: boolean) => void
}

const newManagerSchema = z.object({
  firstName: z.string().trim().min(1, 'required'),
  lastName: z.string().trim(),
})

type NewManagerFormValues = z.infer<typeof newManagerSchema>

export function NewManagerDialog({ onOpenChange }: NewManagerDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const formId = useId()
  const { create, isMutating } = useCreateManager()
  const [submitError, setSubmitError] = useState<PipelineApiError | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewManagerFormValues>({
    resolver: zodResolver(newManagerSchema),
    defaultValues: { firstName: '', lastName: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    // The submit button is disabled while mutating; the Enter key is not.
    if (isMutating) return
    setSubmitError(null)
    try {
      const manager = await create(values)
      onOpenChange(false)
      navigate(`/managers/${manager.id}`)
    } catch (cause) {
      setSubmitError(cause as PipelineApiError)
    }
  })

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>{t('pages.managers.newManager.title')}</DialogTitle>
            <DialogDescription>{t('pages.managers.newManager.description')}</DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="py-6">
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-5">
            <label className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.managers.name.first')}
              </span>
              <Input {...register('firstName')} autoFocus aria-invalid={!!errors.firstName} />
              {errors.firstName && (
                <span role="alert" className="text-[11px] font-medium text-toast-error-text">
                  {t('pages.managers.name.required')}
                </span>
              )}
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.managers.name.last')}
              </span>
              <Input {...register('lastName')} />
            </label>
            {submitError && (
              <p role="alert" className="text-[11px] font-medium text-toast-error-text">
                {t(saveErrorKey(submitError))}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isMutating}
          >
            {t('pages.managers.name.cancel')}
          </Button>
          <Button type="submit" form={formId} variant="primary" size="sm" disabled={isMutating}>
            {t('pages.managers.newManager.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
